// The incremental half of the tiered MSSQL snapshot load (SLICES.md § S7
// items 1 + 2). `mssqlTableCache.ts` owns the cache entry, the TTL and the
// phase orchestration; this module owns the DATA STRUCTURE those phases fill
// in — a "live snapshot" that keeps the native cache shape and its bus-shaped
// projection in lockstep as pages arrive.
//
// Why a live structure instead of re-running `projectMssqlSnapshot` per page:
// on a 20k-table / 200k-column database a full projection is a multi-hundred-
// millisecond synchronous pass. Doing it once per page (dozens of pages)
// would be quadratic AND would blow the 50 ms long-task budget the perf gate
// (`snapshotLoadLongTasks.perf.spec.ts`) asserts. Here each page mutates only
// the table entries it touches and re-projects only those, so the per-page
// main-thread cost scales with the PAGE, not with the database.

import type {
  FkEdge,
  RoutineMeta,
  SchemaSnapshot,
  SequenceMeta,
  TableMeta,
} from '@/core/engines/intellisenseTypes';
import { projectMssqlTableEntry } from '@/features/intellisense/schemaBus/adapters/mssqlAdapter';

import { forEachChunkYielding } from './introspectionPaging';
import type {
  MssqlCheckIntrospection,
  MssqlColumnIntrospection,
} from './mssqlColumnIntrospection';
import type { MssqlIndexIntrospection } from './mssqlIndexIntrospection';
import type {
  MssqlTableColumnEntry,
  MssqlTableEntry,
  MssqlTableSnapshot,
} from './mssqlTableCache';

/**
 * A snapshot under construction: the native cache object, its bus projection,
 * and the bookkeeping that lets a page mutate both in place. `native` and
 * `projected` share their table ordering — index `i` in `native.tables` is
 * index `i` in `projectedTables` — and share the identity of the FK / routine
 * / sequence arrays, so appending to one is visible through both.
 */
export type MssqlLiveSnapshot = {
  readonly native: MssqlTableSnapshot;
  readonly projected: SchemaSnapshot;
  /** Mutable array backing `projected.tables`. */
  readonly projectedTables: TableMeta[];
  /** `schema|name` (lower-cased) → index into `native.tables`. */
  readonly tableIndex: Map<string, number>;
  readonly foreignKeys: FkEdge[];
  readonly routines: RoutineMeta[];
  readonly sequences: SequenceMeta[];
  /** Table indexes whose columns/checks/indexes this refresh has replaced. */
  readonly replacedColumns: Set<number>;
  readonly replacedChecks: Set<number>;
  readonly replacedIndexes: Set<number>;
  /** Whole-database artefacts whose carried-over list this refresh has replaced. */
  readonly replacedGlobals: Set<'foreignKeys' | 'routines' | 'sequences'>;
};

export function tableKey(schema: string, name: string): string {
  return `${schema.toLowerCase()}|${name.toLowerCase()}`;
}

/**
 * Carry the previous refresh's phase-2 payload (columns / CHECKs / indexes)
 * onto the freshly fetched table list — stale-while-revalidate.
 *
 * Without this, every forced refresh would blank the columns of every table
 * for as long as phase 2 takes: the pill would flip back to a tables-only
 * snapshot, `alias.` would fall through to the per-table lazy fetch, and any
 * poller that force-refreshes in a loop (the real-DB e2e readiness gate does
 * exactly that) could never observe a column. The carried data is replaced
 * table by table as phase 2's pages land.
 */
export function carryOverIntrospection(
  previous: readonly MssqlTableEntry[] | undefined,
  fresh: readonly MssqlTableEntry[],
): MssqlTableEntry[] {
  if (!previous || previous.length === 0) return fresh.slice();
  const byKey = new Map<string, MssqlTableEntry>();
  for (const entry of previous) byKey.set(tableKey(entry.schema, entry.name), entry);
  return fresh.map((entry) => {
    const prior = byKey.get(tableKey(entry.schema, entry.name));
    if (!prior) return entry;
    if (!prior.columns && !prior.checkConstraints && !prior.indexes) return entry;
    return {
      ...entry,
      ...(prior.columns ? { columns: prior.columns } : {}),
      ...(prior.checkConstraints ? { checkConstraints: prior.checkConstraints } : {}),
      ...(prior.indexes ? { indexes: prior.indexes } : {}),
    };
  });
}

/**
 * Build the live snapshot for a freshly fetched table list, projecting the
 * tables in event-loop-yielding chunks so even a 200k-table catalog never
 * occupies the main thread for a single long task.
 */
export async function createMssqlLiveSnapshot(
  native: MssqlTableSnapshot,
  options?: { readonly yieldBetweenChunks?: () => Promise<void> },
): Promise<MssqlLiveSnapshot> {
  const projectedTables: TableMeta[] = [];
  const tableIndex = new Map<string, number>();
  const schemaSet = new Set<string>();
  await forEachChunkYielding(
    native.tables,
    (chunk) => {
      for (const entry of chunk) {
        tableIndex.set(tableKey(entry.schema, entry.name), projectedTables.length);
        schemaSet.add(entry.schema);
        projectedTables.push(projectMssqlTableEntry(entry, native.database));
      }
    },
    { yieldBetweenChunks: options?.yieldBetweenChunks },
  );

  const foreignKeys = native.foreignKeys.slice();
  const routines = (native.routines ?? []).slice();
  const sequences = (native.sequences ?? []).slice();
  // The native snapshot shares the mutable arrays so `getCachedMssqlTables`
  // callers see phase 2 fill in without a second copy.
  native.foreignKeys = foreignKeys;
  native.routines = routines;
  native.sequences = sequences;

  const projected: SchemaSnapshot = {
    engineId: 'mssql',
    connectionId: native.connectionId,
    database: native.database,
    schemas: Array.from(schemaSet).sort(),
    tables: projectedTables,
    routines,
    sequences,
    foreignKeys,
    serverVersion: native.serverVersion || undefined,
    mssqlCompatLevel:
      typeof native.mssqlCompatLevel === 'number' ? native.mssqlCompatLevel : undefined,
    ...(native.defaultSchema ? { mssqlDefaultSchema: native.defaultSchema } : {}),
    builtAt: native.builtAt,
  };

  return {
    native,
    projected,
    projectedTables,
    tableIndex,
    foreignKeys,
    routines,
    sequences,
    replacedColumns: new Set<number>(),
    replacedChecks: new Set<number>(),
    replacedIndexes: new Set<number>(),
    replacedGlobals: new Set<'foreignKeys' | 'routines' | 'sequences'>(),
  };
}

/** Re-project one table entry in place after a page mutated it. */
function reproject(live: MssqlLiveSnapshot, index: number): void {
  const entry = live.native.tables[index];
  if (!entry) return;
  live.projectedTables[index] = projectMssqlTableEntry(entry, live.native.database);
}

type PerTableApply<TItem> = {
  readonly keyOf: (item: TItem) => string;
  readonly replaced: (live: MssqlLiveSnapshot) => Set<number>;
  readonly append: (entry: MssqlTableEntry, items: TItem[], replace: boolean) => void;
};

function applyPerTable<TItem>(
  live: MssqlLiveSnapshot,
  items: readonly TItem[],
  spec: PerTableApply<TItem>,
): void {
  if (items.length === 0) return;
  const grouped = new Map<number, TItem[]>();
  for (const item of items) {
    const index = live.tableIndex.get(spec.keyOf(item));
    if (index === undefined) continue;
    const list = grouped.get(index);
    if (list) list.push(item);
    else grouped.set(index, [item]);
  }
  const replaced = spec.replaced(live);
  for (const [index, list] of grouped) {
    const entry = live.native.tables[index];
    if (!entry) continue;
    const replace = !replaced.has(index);
    replaced.add(index);
    spec.append(entry, list, replace);
    reproject(live, index);
  }
}

/** Merge one page of column rows, replacing carried-over columns table by table. */
export function applyColumnPage(
  live: MssqlLiveSnapshot,
  columns: readonly MssqlColumnIntrospection[],
): void {
  applyPerTable(live, columns, {
    keyOf: (c) => tableKey(c.schema, c.table),
    replaced: (l) => l.replacedColumns,
    append: (entry, list, replace) => {
      const projected: MssqlTableColumnEntry[] = list.map((c) => ({
        name: c.name,
        dataType: c.dataType,
        nullable: c.nullable,
        isPrimaryKey: c.isPrimaryKey,
        isIdentity: c.isIdentity,
        isComputed: c.isComputed,
        ordinal: c.ordinal,
      }));
      // Never mutates an existing array: a carried-over `columns` may still be
      // shared with the previous snapshot object a caller is holding.
      entry.columns = replace ? projected : [...(entry.columns ?? []), ...projected];
    },
  });
}

export function applyCheckPage(
  live: MssqlLiveSnapshot,
  checks: readonly MssqlCheckIntrospection[],
): void {
  applyPerTable(live, checks, {
    keyOf: (c) => tableKey(c.schema, c.table),
    replaced: (l) => l.replacedChecks,
    append: (entry, list, replace) => {
      const definitions = list.map((c) => c.definition);
      entry.checkConstraints = replace
        ? definitions
        : [...(entry.checkConstraints ?? []), ...definitions];
    },
  });
}

export function applyIndexPage(
  live: MssqlLiveSnapshot,
  indexes: readonly MssqlIndexIntrospection[],
): void {
  applyPerTable(live, indexes, {
    keyOf: (i) => tableKey(i.schema, i.table),
    replaced: (l) => l.replacedIndexes,
    append: (entry, list, replace) => {
      const metas = list.map((i) => i.index);
      entry.indexes = replace ? metas : [...(entry.indexes ?? []), ...metas];
    },
  });
}

/**
 * After a per-table artefact was read COMPLETELY (`isCompleteOutcome`), every
 * table this refresh did not touch has no rows of it any more — clear what it
 * carried over from the previous refresh. Never call after a failed, aborted
 * or capped fetch: absence of rows then proves nothing (release review
 * 1.5.0, F5). Same contract as `reconcilePerRelationArtefact` in
 * `liveSnapshotMerge.ts`, over this cache's hand-written replaced-sets.
 */
function reconcilePerTable(
  live: MssqlLiveSnapshot,
  replaced: Set<number>,
  clear: (entry: MssqlTableEntry) => boolean,
): void {
  for (let index = 0; index < live.native.tables.length; index += 1) {
    if (replaced.has(index)) continue;
    const entry = live.native.tables[index];
    if (!entry || !clear(entry)) continue;
    reproject(live, index);
  }
}

export function reconcileChecks(live: MssqlLiveSnapshot): void {
  reconcilePerTable(live, live.replacedChecks, (entry) => {
    if (!entry.checkConstraints || entry.checkConstraints.length === 0) return false;
    delete entry.checkConstraints;
    return true;
  });
}

export function reconcileIndexes(live: MssqlLiveSnapshot): void {
  reconcilePerTable(live, live.replacedIndexes, (entry) => {
    if (!entry.indexes || entry.indexes.length === 0) return false;
    delete entry.indexes;
    return true;
  });
}

/**
 * Whole-database artefacts: the FIRST page of a refresh drops the carried-over
 * list (so a deleted FK / routine / sequence disappears), later pages append.
 * A fetch that fails before its first page leaves the carried list standing —
 * stale beats empty for a completion vocabulary. A fetch that SUCCEEDS with
 * zero rows reaches here with `items = []` (the pager guarantees one sink
 * call) and clears the carried list.
 */
function applyGlobalPage<TItem>(
  live: MssqlLiveSnapshot,
  artefact: 'foreignKeys' | 'routines' | 'sequences',
  target: TItem[],
  items: readonly TItem[],
): void {
  if (!live.replacedGlobals.has(artefact)) {
    live.replacedGlobals.add(artefact);
    target.length = 0;
  }
  target.push(...items);
}

export function applyForeignKeyPage(live: MssqlLiveSnapshot, edges: readonly FkEdge[]): void {
  applyGlobalPage(live, 'foreignKeys', live.foreignKeys, edges);
}

export function applyRoutinePage(live: MssqlLiveSnapshot, routines: readonly RoutineMeta[]): void {
  applyGlobalPage(live, 'routines', live.routines, routines);
}

export function applySequencePage(
  live: MssqlLiveSnapshot,
  sequences: readonly SequenceMeta[],
): void {
  applyGlobalPage(live, 'sequences', live.sequences, sequences);
}
